import {
	Dialog,
	Form,
	FormattedText,
	FormItem,
	Toolbar
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	ItemPicker,
	CommonItemPickerOptions,
	ItemPickerOptions,
	SharePointItemPickerOptions,
	WebDavItemPickerOptions,
	ItemPickerLocation,
	ItemPickerStoreKind,
	ItemPickerMode
} from "../ItemPicker/ItemPicker";
import { isNil, isNotNil } from "client/Misc/utils";
import { FileViewModel } from "client/Api/Documents/SharePoint/common";
import { IFormHandler, useFormHandler } from "client/Forms";
import { getExtension, getFileNameWithoutExtension } from "client_legacy/Documents/path";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { DocumentStoreType } from "client/Api/Documents/common";
import { never } from "client_legacy/ndaw/errors";
import {
	DocumentOpenPossibilities,
	calculate as calculateSharePointOpenPossibilities
} from "client_legacy/Documents/SharePoint/documentOpenPossibilities";
import { openFileInWopi } from "client_legacy/Documents/SharePoint/documentOpener";
import { downloadDocumentInline } from "client_legacy/Documents/SharePoint/Actions/downloadAction";
import { getSharePointLocationUrl, getWebDavFolderUrl } from "client/Features/Documents/routing";
import { createUrlRelativeToEnvironment, navigateTo } from "client_legacy/ndaw/navigator";
import {
	OpenFilePossibilitiesProvider as WebDavOpenFilePossibilitiesProvider,
	OpenPossibilities as WebDavOpenPossibilities
} from "client/Features/Documents/WebDav/openFilePossibilitiesProvider";
import { openInline } from "client_legacy/ndaw/fileDownloader";
import { open as webDavWopiOpener } from "client_legacy/Documents/WebDav/wopiOpener";
import { TextInputWithExtension } from "./TextInputWithExtension";

export type SaveAttachmentDialogProps = {
	onClose: () => void;
	name: string;
	onSave: OnSaveHandler;
};

export const SaveAttachmentDialog = (props: SaveAttachmentDialogProps): ReactElement => {
	const {
		onClose,
		name,
		onSave
	} = props;

	const [location, setLocation] = useState<ItemPickerLocation | undefined>(undefined);
	const [formData, setFormData] = useState<SaveAttachmentData>(() => ({
		name: getFileNameWithoutExtension(name),
		location: undefined
	}));
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const alertManager: AlertManager = useAlerts();

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const formHandler: IFormHandler<SaveAttachmentData> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const fileExtension: string = getExtension(name);

	const isButtonDisabled: boolean = isNil(location);

	const itemPickerOptions: ItemPickerOptions = useMemo(
		() => {
			const { sharePoint, webDav } = documentsConfiguration();

			const sharePointOptions: SharePointItemPickerOptions = sharePoint.isConfigured
				? {
					isEnabled: true,
					shouldShowPersonalViews: true,
					availableDestination: { kind: "all" }
				}
				: { isEnabled: false };

			const webDavOptions: WebDavItemPickerOptions = webDav && webDav.isConfigured
				? {
					isEnabled: true,
					availableDestination: { kind: "all" }
				}
				: { isEnabled: false };

			const commonOptions: CommonItemPickerOptions = {
				mode: ItemPickerMode.SingleFolder,
				creationOptions: {
					folder: true
				}
			};

			return {
				sharePointOptions,
				webDavOptions,
				commonOptions
			};
		},
		[]
	);

	const handleFileSavedAlertAndActions = useCallback(
		(saveAttachmentResponseAndKind: SaveAttachmentResponseAndKind): void => {
			let canBeOpened: boolean;
			let openFileHandler: undefined | (() => void);
			let openLocationHandler: () => void;

			switch (saveAttachmentResponseAndKind.kind) {
				case DocumentStoreType.SharePoint:
					const { file } = saveAttachmentResponseAndKind.model as SaveAsAttachmentToSharePointResponseModel;
					const sharePointOpenPossibilities: DocumentOpenPossibilities = calculateSharePointOpenPossibilities(file);

					canBeOpened = sharePointOpenPossibilities.canBeOpenedOnline
						|| sharePointOpenPossibilities.canBeOpenedInTheBrowser;

					if (sharePointOpenPossibilities.canBeOpenedOnline) {
						openFileHandler = () => openFileInWopi(file);
					} else if (sharePointOpenPossibilities.canBeOpenedInTheBrowser) {
						openFileHandler = () => downloadDocumentInline(file);
					}

					openLocationHandler = () => navigateTo(
						getSharePointLocationUrl(file.location, file.parentFolderId),
						true
					);

					break;
				case DocumentStoreType.WebDav:
					const { webDav: webDavConfiguration } = documentsConfiguration();
					if (!webDavConfiguration) {
						throw new Error("WebDav is not available");
					}

					const webDavFile = saveAttachmentResponseAndKind.model as SaveAsAttachmentToWebDavResponseModel;

					const webDavOpenPossibilities: WebDavOpenPossibilities = new WebDavOpenFilePossibilitiesProvider()
						.getForFileName(webDavFile.name, webDavConfiguration);

					canBeOpened = webDavOpenPossibilities.wopi || webDavOpenPossibilities.browser;
					if (webDavOpenPossibilities.wopi) {
						openFileHandler = () => webDavWopiOpener(
							webDavFile.serverId,
							webDavFile.id,
							webDavFile.name
						);
					} else {
						openFileHandler = () => openInline(
							createUrlRelativeToEnvironment(
								`/Documents/WebDav/${webDavFile.serverId}/OpenFileInline?fileId=${encodeURIComponent(webDavFile.id)}`
							)
						);
					}

					openLocationHandler = () => navigateTo(
						getWebDavFolderUrl(webDavFile.serverId, webDavFile.parentFolderId),
						true
					);

					break;
				default:
					never(saveAttachmentResponseAndKind.kind);
			}

			alertManager.showSuccess(
				canBeOpened
					? (
						<FormattedText
							text={localize("_SaveAsSuccessful")}
							thingsToReplace={[
								{ what: "{{OpenFile}}", filler: <a onClick={openFileHandler}>{localize("_OpenFile")}</a> },
								{ what: "{{OpenFileLocation}}", filler: <a onClick={openLocationHandler}>{localize("_OpenFileLocation")}</a> }
							]}
						/>
					)
					: (
						<FormattedText
							text={localize("_YourAttachmentHasBenSavedSuccessfullyOpenFileLocation")}
							thingsToReplace={[
								{ what: "{{OpenFileLocation}}", filler: <a onClick={openLocationHandler}>{localize("_OpenFileLocation")}</a> }
							]}
						/>
					)
			);
		},
		[alertManager]
	);

	const doneHandler = useCallback(
		async (): Promise<void> => {
			if (isNotNil(location)) {
				try {
					setIsBusy(true);
					const response: SaveAsAttachmentResponseModel = await onSave({
						name: formHandler.value.name + fileExtension,
						location
					});

					handleFileSavedAlertAndActions({
						kind: location.kind,
						model: response
					});
					setIsBusy(false);
					onClose();
				} catch (e) {
					setIsBusy(false);

					if (formHandler.validation.tryProcessServerError(e)) {
						return;
					}
					alertManager.showError(localize("_AttachmentSaveFailed"));
				}
			}
		},
		[
			alertManager,
			fileExtension,
			formHandler.validation,
			formHandler.value.name,
			handleFileSavedAlertAndActions,
			location,
			onClose,
			onSave
		]
	);

	return (
		<Dialog
			trackingId="save-attachment-dialog"
			hasBusyIndicator={isBusy}
			variant="left-slider"
			onClose={onClose}
		>
			<Dialog.Header
				title={localize("_SaveAs")}
				onClose={onClose}
				hasBorder
			/>
			<Dialog.Body goesEdgeToEdge>
				<ItemPicker
					options={itemPickerOptions}
					location={location}
					onLocationChange={setLocation}
				/>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Form onSubmit="NONE" hasWidthConstrained>
					<FormItem
						label={localize("_DocumentName")}
						isRequired
					>
						<TextInputWithExtension
							attachable={formHandler.for("name")}
							isMobile={isMobile}
							fileExtension={fileExtension}
						/>
					</FormItem>
				</Form>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={doneHandler}
						text={localize("_Done")}
						isDisabled={isButtonDisabled}
						trackingId="done"
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Cancel")}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

SaveAttachmentDialog.displayName="SaveAttachmentDialog";

export interface SaveAsAttachmentToSharePointResponseModel {
	file: FileViewModel;
}

export type SaveAsAttachmentResponseModel =
	| SaveAsAttachmentToSharePointResponseModel
	| SaveAsAttachmentToWebDavResponseModel;

type SaveAttachmentResponseAndKind = {
	kind: ItemPickerStoreKind;
	model: SaveAsAttachmentResponseModel;
};

export interface SaveAsAttachmentToWebDavResponseModel {
	serverId: number;
	parentFolderId: string;
	id: string;
	name: string;
}

type OnSaveHandler = (saveData: SaveAttachmentData) => Promise<SaveAsAttachmentResponseModel>;

export type SaveAttachmentData = {
	name: string;
	location: ItemPickerLocation | undefined;
};

type SaveAttachmentDialogInitialData = Omit<SaveAttachmentDialogProps, "onClose">;

type DialogState = {
	isOpen: false;
} | {
	isOpen: true;
} & SaveAttachmentDialogInitialData;

export function useSaveAttachmentDialog(
): [
	dialogElement: ReactElement | null,
	open: (openData: SaveAttachmentDialogInitialData) => void
] {
	const [dialogState, setDialogState] = useState<DialogState>({isOpen: false});

	const renderDialog = useCallback(
		(): ReactElement | null => {
			return dialogState.isOpen
				? (
					<SaveAttachmentDialog
						name={dialogState.name}
						onSave={dialogState.onSave}
						onClose={() => setDialogState({ isOpen: false })}
					/>
				)
				: null;
		},
		[dialogState]
	);

	const handleOpenDialog = useCallback(
		(props: SaveAttachmentDialogInitialData): void => {
			setDialogState({
				isOpen: true,
				name: props.name,
				onSave: props.onSave
			});
		},
		[]
	);

	return [
		renderDialog(),
		handleOpenDialog
	];
}